/**
 * iter285 — Ladies Hub category icon resolver.
 *
 * The operator asked for a more premium, elegant look for the Ladies Hub
 * category icons (which previously used emoji 👗👠👜 from
 * `/app/frontend/src/hubs/ladies/LadiesHubCategories.json`).
 *
 * This module maps each canonical category ID → a Lucide icon component
 * (already bundled as a dependency in `package.json`). Lucide gives us:
 *   • Crisp vector icons at any size — no AI art, no image assets.
 *   • A consistent stroke style across the whole hub.
 *   • Zero net bundle cost (tree-shaken; we only pull the ones we use).
 *
 * Usage:
 *   const Icon = getLadiesIcon(category.id);
 *   return <Icon size={20} strokeWidth={2} />;
 *
 * If the ID isn't mapped (new category added without a code release),
 * `null` is returned and the consumer should fall back to the emoji
 * stored on the category record. This keeps the change backwards-safe.
 */
import {
  Shirt,        // fashion (top-level + women's clothing)
  Footprints,   // shoes / footwear
  Watch,        // watches
  Gem,          // accessories / jewellery
  ShoppingBag,  // bags / handbags
  Sparkles,     // beauty / perfumes / cosmetics
  Heart,        // wellness / personal care / lingerie
  Baby,         // children / kids / maternity
  Scissors,     // tailoring / alterations
  Crown,        // bridal / occasion
  GraduationCap,// hobbies / classes
  Briefcase,    // services
  Tag,          // generic / other
} from 'lucide-react';

/**
 * Map of canonical Ladies Hub category IDs to Lucide icon components.
 * Keys MUST match the `id` field in `LadiesHubCategories.json`.
 *
 * Add a new line here whenever a new ladies category is introduced so
 * the chip bar renders the elegant vector icon instead of an emoji.
 */
export const LADIES_LUCIDE_ICON_MAP = {
  // Fashion / clothing
  fashion: Shirt,
  clothing: Shirt,
  abayas: Shirt,
  dresses: Shirt,
  tops: Shirt,
  // Footwear
  shoes: Footprints,
  footwear: Footprints,
  heels: Footprints,
  // Accessories / jewellery / watches
  accessories: Gem,
  jewelry: Gem,
  jewellery: Gem,
  watches: Watch,
  bags: ShoppingBag,
  handbags: ShoppingBag,
  // Beauty
  beauty: Sparkles,
  cosmetics: Sparkles,
  perfumes: Sparkles,
  makeup: Sparkles,
  // Personal & wellness
  wellness: Heart,
  lingerie: Heart,
  personal: Heart,
  // Children / family
  children: Baby,
  kids: Baby,
  baby: Baby,
  maternity: Baby,
  // Bridal / occasion
  bridal: Crown,
  occasion: Crown,
  // Services & hobbies
  tailoring: Scissors,
  hobbies: GraduationCap,
  services: Briefcase,
  // Generic
  other: Tag,
};

/**
 * Resolve a Lucide icon component for a Ladies Hub category id/key.
 * Returns `null` (NOT a default) when there is no mapping so the
 * caller can decide whether to fall back to emoji or skip rendering.
 *
 * @param {string} categoryId — the `id` from LadiesHubCategories.json
 * @returns {React.ComponentType|null}
 */
export function getLadiesIcon(categoryId) {
  if (!categoryId) return null;
  return LADIES_LUCIDE_ICON_MAP[String(categoryId).toLowerCase()] || null;
}

/**
 * Convenience helper used by chip rendering — returns the icon component
 * if mapped, otherwise the original emoji string. Always returns SOMETHING
 * the consumer can render.
 */
export function resolveLadiesChipIcon(categoryId, fallbackEmoji) {
  const Component = getLadiesIcon(categoryId);
  return Component || fallbackEmoji || null;
}
